import {
  Anchor,
  Badge,
  Box,
  Checkbox,
  Group,
  Paper,
  Popover,
  Tooltip
} from '@mantine/core'
import { useForm } from '@mantine/form'
import {
  IconBarcode,
  IconProps,
  IconTextRecognition
} from '@tabler/icons-react'
import {
  ReactElement,
  ReactNode,
  cloneElement,
  useEffect,
  useState
} from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { OcrAnnotationInfo } from '@/components/annotations/OcrAnnotationInfo/OcrAnnotationInfo'
import { QrBarcodeAnnotationInfo } from '@/components/annotations/QrBarcodeAnnotationInfo/QrBarcodeAnnotationInfo'
import { LabelMultiSelect } from '@/components/labels/LabelMultiSelect/LabelMultiSelect'
import { Label } from '@/types/label'
import {
  LabelGroupPopover,
  LabelGroupPopoverButtons,
  LabelGroupPopoverHeader
} from '../LabelGroupPopover/LabelGroupPopover'

type ExtractionTypeConfig = Record<
  'ocr' | 'qr_barcode',
  {
    icon: ReactElement<IconProps>
    title: ReactNode
    banner: ReactNode
    checkboxLabel: ReactNode
    disabledTooltip: ReactNode
  }
>

const EXTRACTION_TYPE_CONFIG: ExtractionTypeConfig = {
  ocr: {
    icon: <IconTextRecognition />,
    title: <FormattedMessage id="logic.extraction.ocr.title" />,
    banner: <OcrAnnotationInfo />,
    checkboxLabel: (
      <FormattedMessage id="logic.extraction.ocr.checkbox.label" />
    ),
    disabledTooltip: (
      <FormattedMessage id="logic.extraction.ocr.disabledTooltip" />
    )
  },
  qr_barcode: {
    icon: <IconBarcode />,
    title: <FormattedMessage id="logic.extraction.qr_barcode.title" />,
    banner: <QrBarcodeAnnotationInfo />,
    checkboxLabel: (
      <FormattedMessage id="logic.extraction.qr_barcode.checkbox.label" />
    ),
    disabledTooltip: (
      <FormattedMessage id="logic.extraction.qr_barcode.disabledTooltip" />
    )
  }
}

export type ExtractionConfigValues = {
  isExtractionEnabled: boolean
  extractionLabelIds: string[]
}

type ExtractionConfig = {
  type: 'ocr' | 'qr_barcode'
  extractionConfig: ExtractionConfigValues
  labels: Label[]
  disabled?: boolean
  onChange: (config: ExtractionConfigValues) => void
}

export const ExtractionConfig = ({
  type,
  extractionConfig,
  labels,
  disabled,
  onChange
}: ExtractionConfig) => {
  const intl = useIntl()

  const [isPopoverOpened, setIsPopoverOpened] = useState(false)

  const { title, banner, checkboxLabel, disabledTooltip, icon } =
    EXTRACTION_TYPE_CONFIG[type]

  const form = useForm<ExtractionConfigValues>({
    initialValues: {
      ...extractionConfig
    },
    validate: {
      extractionLabelIds: (value, values) => {
        if (values.isExtractionEnabled && value.length === 0) {
          return intl.formatMessage({ id: 'logic.extraction.select.required' })
        }

        return null
      }
    }
  })

  useEffect(() => {
    form.setValues(extractionConfig)
    // do not add form as effect dependency https://github.com/mantinedev/mantine/issues/5338#issuecomment-1837468066
  }, [extractionConfig]) // eslint-disable-line react-hooks/exhaustive-deps

  const options = labels.map((label) => ({
    labelId: label.id,
    name: label.name,
    color: label.color
  }))

  const resetAndClose = () => {
    form.setValues(extractionConfig)
    setIsPopoverOpened(false)
  }

  const handleSave = form.onSubmit((values) => {
    onChange({
      ...values,
      extractionLabelIds: values.isExtractionEnabled
        ? values.extractionLabelIds
        : []
    })

    setIsPopoverOpened(false)
  })

  const handlePopoverChange = (opened: boolean) => {
    if (!opened) {
      resetAndClose()
    } else {
      setIsPopoverOpened(true)
    }
  }

  const handleExtractionEnabledChange = (enabled: boolean) => {
    form.setFieldValue('isExtractionEnabled', enabled)

    if (enabled && form.getValues().extractionLabelIds.length === 0) {
      // select all labels if extraction is enabled and none are chosen
      form.setFieldValue(
        'extractionLabelIds',
        labels.map((label) => label.id)
      )
    }
  }

  return (
    <LabelGroupPopover opened={isPopoverOpened} onChange={handlePopoverChange}>
      <Popover.Target>
        <Tooltip w={300} label={disabledTooltip} disabled={!disabled} multiline>
          <Anchor
            component="button"
            type="button"
            disabled={disabled}
            size="xs"
            styles={{
              root: {
                textTransform: 'lowercase',
                cursor: disabled ? 'not-allowed' : 'pointer'
              }
            }}
            onClick={() => handlePopoverChange(!isPopoverOpened)}
          >
            <Group wrap="nowrap" gap={4} align="center">
              {cloneElement(icon, { size: 14 })}

              {title}

              {!extractionConfig.isExtractionEnabled && (
                <Badge size="xs" fz={8} color="gray">
                  <FormattedMessage id={'logic.alert.no'} />
                </Badge>
              )}

              {extractionConfig.isExtractionEnabled &&
                extractionConfig.extractionLabelIds.map((labelId) => {
                  const label = labels.find((l) => l.id === labelId)

                  return label ? (
                    <Badge key={labelId} size="xs" color={label.color} fz={8}>
                      {label.name}
                    </Badge>
                  ) : null
                })}
            </Group>
          </Anchor>
        </Tooltip>
      </Popover.Target>

      <Popover.Dropdown>
        <LabelGroupPopoverHeader
          title={title}
          icon={cloneElement(icon, { size: 20 })}
          onClose={resetAndClose}
        />

        {banner}

        <Paper bg="gray.1" p="sm" mt="md">
          <Checkbox
            checked={form.getValues().isExtractionEnabled}
            label={checkboxLabel}
            onChange={(event) =>
              handleExtractionEnabledChange(event.currentTarget.checked)
            }
          />

          {form.getValues().isExtractionEnabled && (
            <Box mt="xl">
              <LabelMultiSelect
                values={form.getValues().extractionLabelIds}
                options={options}
                label={<FormattedMessage id="logic.extraction.select.label" />}
                error={form.errors.extractionLabelIds}
                withinPortal={false}
                onChange={(value) =>
                  form.setFieldValue('extractionLabelIds', value)
                }
              />
            </Box>
          )}
        </Paper>

        <LabelGroupPopoverButtons
          onCancel={resetAndClose}
          onSave={handleSave}
        />
      </Popover.Dropdown>
    </LabelGroupPopover>
  )
}
